import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'

// Lazy-load Firebase-Funktionen, um initiales Bundle zu reduzieren
import type { User } from 'firebase/auth'
import type { SiteImage } from '../firebase/images'

let onAuthStateChange: ((callback: (user: User | null) => void) => () => void) | null = null
let getCurrentUser: (() => User | null) | null = null
let getPublicImageByName: ((category: 'home' | 'about' | 'floating', name: string) => Promise<SiteImage | null>) | null = null
let getPublicImagesByCategory: ((category: 'home' | 'about' | 'floating') => Promise<SiteImage[]>) | null = null

// Lade Firebase-Funktionen bei Bedarf
const loadFirebaseAuth = async () => {
  if (!onAuthStateChange || !getCurrentUser) {
    const authModule = await import('../firebase/auth')
    onAuthStateChange = authModule.onAuthStateChange
    getCurrentUser = authModule.getCurrentUser
  }
  return { onAuthStateChange: onAuthStateChange!, getCurrentUser: getCurrentUser! }
}

const loadFirebaseImages = async () => {
  if (!getPublicImageByName || !getPublicImagesByCategory) {
    const imagesModule = await import('../firebase/images')
    getPublicImageByName = imagesModule.getPublicImageByName
    getPublicImagesByCategory = imagesModule.getPublicImagesByCategory
  }
  return { getPublicImageByName: getPublicImageByName!, getPublicImagesByCategory: getPublicImagesByCategory! }
}

// Preload-Funktion für Bilder mit automatischer Bereinigung
const addImagePreload = (url: string) => {
  if (typeof document === 'undefined') return
  
  const existingPreload = document.querySelector(`link[rel="preload"][as="image"][href="${url}"]`)
  if (existingPreload) return
  
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = url
  link.setAttribute('fetchpriority', 'high')
  document.head.appendChild(link)
  
  // Entferne Preload-Link, sobald das Bild geladen wurde oder nach 5 Sekunden
  const img = new Image()
  const timeout = setTimeout(() => {
    link.remove()
  }, 5000) // Entferne nach 5 Sekunden, falls nicht verwendet
  
  img.onload = () => {
    clearTimeout(timeout)
    // Entferne Preload-Link, sobald Bild geladen wurde
    link.remove()
  }
  img.onerror = () => {
    clearTimeout(timeout)
    link.remove()
  }
  img.src = url
}

// Preload About-Bild beim Navigieren zur About-Seite
const preloadAboutImage = async () => {
  try {
    const { getPublicImageByName: getImage, getPublicImagesByCategory: getImages } = await loadFirebaseImages()
    const profileImage = await getImage!('about', 'profile')
    if (profileImage) {
      addImagePreload(profileImage.url)
    } else {
      const images = await getImages!('about')
      if (images.length > 0) {
        addImagePreload(images[0].url)
      }
    }
  } catch (error) {
    // Fehler beim Preload ignorieren - Bild wird später normal geladen
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/About.vue'),
      meta: {
        title: 'Über mich | Webdesigner & QA Specialist München - Maximilian Reitsberger',
        description: 'Maximilian Reitsberger – Fachinformatiker und ISTQB-zertifizierter Softwaretester aus München. Professionelles Webdesign und Webentwicklung mit QA-Expertise.'
      }
    },
    {
      path: '/portfolio',
      name: 'portfolio',
      component: () => import('../views/Portfolio.vue'),
      meta: {
        title: 'Webdesign-Projekte München | Portfolio - Maximilian Reitsberger',
        description: 'Referenzen und Projekte von Maximilian Reitsberger: WordPress-Websites, individuelle Webanwendungen und Custom Tools aus München.'
      }
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('../views/Contact.vue'),
      meta: {
        title: 'Kontakt | Webdesign München - Kostenlose Beratung | Maximilian Reitsberger',
        description: 'Kontaktieren Sie Maximilian Reitsberger für professionelles Webdesign in München. Kostenlose Erstberatung – unverbindlich und individuell.'
      }
    },
    {
      path: '/impressum',
      name: 'impressum',
      component: () => import('../views/Impressum.vue'),
      meta: {
        title: 'Impressum - Maximilian Reitsberger',
        description: 'Impressum und rechtliche Angaben zu dieser Website'
      }
    },
    {
      path: '/datenschutz',
      name: 'datenschutz',
      component: () => import('../views/Datenschutz.vue'),
      meta: {
        title: 'Datenschutzerklärung - Maximilian Reitsberger',
        description: 'Datenschutzerklärung und Informationen zur Datenverarbeitung auf dieser Website'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Login.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/admin',
      name: 'admin',
      redirect: '/admin/projects',
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/projects',
      name: 'admin-projects',
      component: () => import('../views/Admin.vue'),
      meta: { 
        requiresAuth: true,
        adminTab: 'projects'
      }
    },
    {
      path: '/admin/testimonials',
      name: 'admin-testimonials',
      component: () => import('../views/Admin.vue'),
      meta: { 
        requiresAuth: true,
        adminTab: 'testimonials'
      }
    },
    {
      path: '/admin/contact-form',
      name: 'admin-contact-form',
      redirect: '/admin/contact-form/submissions',
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/contact-form/:subtab',
      name: 'admin-contact-form-subtab',
      component: () => import('../views/Admin.vue'),
      meta: { 
        requiresAuth: true,
        adminTab: 'contact-form'
      }
    },
    {
      path: '/admin/settings',
      name: 'admin-settings',
      redirect: '/admin/settings/images',
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/settings/:subtab',
      name: 'admin-settings-subtab',
      component: () => import('../views/Admin.vue'),
      meta: { 
        requiresAuth: true,
        adminTab: 'settings'
      }
    },
    {
      path: '/wp-login',
      name: 'wp-login',
      component: () => import('../views/WpLogin.vue'),
      meta: {
        title: 'WordPress Login - Maximilian Reitsberger',
        description: 'Das ist keine WordPress-Seite, aber ich kann dich gerne bei WordPress-Beratungen unterstützen!'
      }
    },
    {
      path: '/wp-admin',
      name: 'wp-admin',
      component: () => import('../views/WpLogin.vue'),
      meta: {
        title: 'WordPress Admin - Maximilian Reitsberger',
        description: 'Das ist keine WordPress-Seite, aber ich kann dich gerne bei WordPress-Beratungen unterstützen!'
      }
    },
    {
      path: '/projects/m-drei',
      name: 'm-drei',
      component: () => import('../views/projects/M-Drei.vue'),
      meta: {
        title: 'M-Drei Projekt | Webentwicklung München - Maximilian Reitsberger',
        description: 'M-Drei Projekt – Innovative Webanwendung mit modernen Technologien. Entwickelt von Maximilian Reitsberger, Webdesigner aus München.'
      }
    },
    {
      path: '/projects/reitsberger-hof',
      name: 'reitsberger-hof',
      component: () => import('../views/projects/Reitsberger-Hof.vue'),
      meta: {
        title: 'Reitsberger Hof | WordPress-Website München - Maximilian Reitsberger',
        description: 'Reitsberger Hof – Moderne WordPress-Webpräsenz für einen familiengeführten Erlebnisbauernhof. Webdesign von Maximilian Reitsberger aus München.'
      }
    },
    {
      path: '/projects/verkaufszaehler',
      name: 'verkaufszaehler',
      component: () => import('../views/projects/Verkaufszähler.vue'),
      meta: {
        title: 'Verkaufszähler | Custom Web-Tool München - Maximilian Reitsberger',
        description: 'Verkaufszähler – Modernes Kassensystem als Web-App für den mobilen Einsatz auf Tablets. Entwickelt von Maximilian Reitsberger aus München.'
      }
    },
    {
      path: '/projects/bey-hub',
      name: 'bey-hub',
      component: () => import('../views/projects/BeyHub.vue'),
      meta: {
        title: 'BEY-Hub.de | Webanwendung München - Maximilian Reitsberger',
        description: 'BEY-Hub.de – Community-Webanwendung mit Teile-Verwaltung und Turnier-Organisation. Webentwicklung von Maximilian Reitsberger aus München.'
      }
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    // Wenn eine gespeicherte Position vorhanden ist (z.B. Browser-Zurück-Button), diese verwenden
    if (savedPosition) {
      return savedPosition
    }
    
    // Wenn ein Hash vorhanden ist, Browser-Standard-Verhalten verwenden
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    
    // Beim initialen Laden (from.name ist undefined) oder bei Navigation immer nach oben scrollen
    // instant für sofortiges Scrollen beim Laden
    if (!from.name || to.path !== from.path) {
      return { top: 0, behavior: 'instant' }
    }
    // Fallback: immer nach oben
    return { top: 0, behavior: 'instant' }
  }
})

// Route Guard
router.beforeEach((to, _from, next) => {
  // Preload About-Bild wenn zur About-Seite navigiert wird
  if (to.path === '/about' || to.name === 'about') {
    preloadAboutImage()
  }
  
  // Setze Fallback-Farbe für Projektseiten SOFORT, bevor Komponenten rendern
  // Dies verhindert das rote Flackern in der Navigation
  if (typeof document !== 'undefined' && to.path.startsWith('/projects/')) {
    if (to.path.includes('bey-hub')) {
      document.documentElement.style.setProperty('--fallback-project-accent', '#2c6ae8')
      document.documentElement.style.setProperty('--project-accent', '#2c6ae8')
    } else if (to.path.includes('m-drei')) {
      document.documentElement.style.setProperty('--fallback-project-accent', '#a50e34')
      document.documentElement.style.setProperty('--project-accent', '#a50e34')
    } else {
      document.documentElement.style.setProperty('--fallback-project-accent', '#a50e34')
      document.documentElement.style.setProperty('--project-accent', '#a50e34')
    }
  } else if (typeof document !== 'undefined') {
    // Entferne CSS-Variablen wenn nicht auf Projektseite
    document.documentElement.style.removeProperty('--project-accent')
    document.documentElement.style.removeProperty('--fallback-project-accent')
  }
  
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest)

  if (!requiresAuth && !requiresGuest) {
    next()
    return
  }

  // Lade Firebase Auth bei Bedarf (lazy-load)
  loadFirebaseAuth().then(({ getCurrentUser: getCurrentUserFn, onAuthStateChange: onAuthStateChangeFn }) => {
    // Prüfe aktuellen Auth-Status
    const currentUser = getCurrentUserFn!()
    
    if (requiresAuth && !currentUser) {
      next('/login')
    } else if (requiresGuest && currentUser) {
      next('/admin/projects')
    } else {
      // Warte auf Auth-State-Änderung für den ersten Check
      const unsubscribe = onAuthStateChangeFn!((user) => {
        unsubscribe()
        if (requiresAuth && !user) {
          next('/login')
        } else if (requiresGuest && user) {
          next('/admin/projects')
        } else {
          next()
        }
      })
    }
  }).catch(() => {
    // Bei Fehler weiterleiten, um hängende Navigation zu vermeiden
    if (requiresAuth) {
      next('/login')
    } else {
      next()
    }
  })
})

// Meta-Tags aktualisieren
router.afterEach((to) => {
  // Titel aktualisieren
  if (to.meta.title) {
    document.title = to.meta.title as string
  } else {
    document.title = 'Webdesign München | Maximilian Reitsberger - Professionelle Websites'
  }

  // Meta-Description aktualisieren
  let metaDescription = document.querySelector('meta[name="description"]')
  if (!metaDescription) {
    metaDescription = document.createElement('meta')
    metaDescription.setAttribute('name', 'description')
    document.head.appendChild(metaDescription)
  }

  if (to.meta.description) {
    metaDescription.setAttribute('content', to.meta.description as string)
  } else {
    metaDescription.setAttribute('content', 'Professionelles Webdesign aus München von Maximilian Reitsberger. Individuelle WordPress-Websites und maßgeschneiderte Webanwendungen mit QA-Expertise.')
  }

  // Canonical URL dynamisch aktualisieren
  let canonical = document.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', `https://reitsberger.de${to.path}`)
})

/**
 * Gibt alle Projektseiten-Routen zurück
 * Wird automatisch aus den definierten Routen extrahiert
 */
export const getProjectPageRoutes = () => {
  return router.getRoutes()
    .filter(route => route.path.startsWith('/projects/'))
    .map(route => {
      // Extrahiere den Namen aus meta.title oder verwende den Routennamen
      let name = (route.meta?.title as string) || route.name?.toString() || route.path
      
      // Entferne " - Maximilian Reitsberger" falls vorhanden
      name = name.replace(' - Maximilian Reitsberger', '').trim()
      
      // Falls kein title vorhanden, formatiere den Routennamen oder Pfad
      if (!route.meta?.title) {
        // Versuche aus dem Routennamen einen lesbaren Namen zu machen
        if (route.name) {
          name = route.name.toString()
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        } else {
          // Extrahiere aus dem Pfad (z.B. '/projects/m-drei' -> 'M-Drei')
          const pathParts = route.path.split('/').filter(Boolean)
          if (pathParts.length > 1) {
            name = pathParts[pathParts.length - 1]
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
          }
        }
      }
      
      return {
        path: route.path,
        name: name,
        description: (route.meta?.description as string) || undefined
      }
    })
}

export default router

